import { HttpsCallableOptions, HttpsCallableResult } from 'firebase/functions'
import { useCallback, useState } from 'react'
import { callableRef } from './callableRef'

export type CallableRefHook<Req, Res> = [
  (data?: Req) => Promise<HttpsCallableResult<Res> | undefined>,
  boolean,
  Error | undefined,
]

/**
 * Strangler-facade analogue of react-firebase-hooks' useHttpsCallable: identical
 * [invoke, loading, error] contract and identical resolve-undefined-on-error semantics,
 * but routes through callableRef (/api/<name>) so a Hosting rewrite decides whether the old
 * Cloud Function or gc-api serves the call. Drop-in swap for useHttpsCallable.
 */
export function useCallableRef<Req = unknown, Res = unknown>(
  name: string,
  options?: HttpsCallableOptions
): CallableRefHook<Req, Res> {
  const [error, setError] = useState<Error>()
  const [loading, setLoading] = useState<boolean>(false)

  const invoke = useCallback(
    async (data?: Req): Promise<HttpsCallableResult<Res> | undefined> => {
      const callable = callableRef<Req, Res>(name, options)
      setLoading(true)
      setError(undefined)
      try {
        return await callable(data)
      } catch (err) {
        // Mirror useHttpsCallable: swallow the rejection, surface it via the error slot,
        // and resolve undefined so existing call sites keep their pre-flip control flow.
        setError(err as Error)
        return undefined
      } finally {
        setLoading(false)
      }
    },
    [name, options]
  )

  return [invoke, loading, error]
}
